import React, { useEffect, useMemo, useState } from "react";
import HomeActionBanner from "./HomeActionBanner";
import PlatterVideoOverlay from "./PlatterVideoOverlay";

const SHOW_PLATTER_VIDEO_HOME = false;

/**
 * ============================================================
 * CONFIG — LANGUES ET IMAGES HERO
 * ============================================================
 */
const SUPPORTED_HERO_LANGS = ["fr", "de", "en"];
const HERO_IMAGE_BASE_URL = "https://app.marchedeluxe.ch/images";

/**
 * ============================================================
 * HELPER — LANGUE ACTIVE
 * ============================================================
 */
function getHeroLang(t) {
  const rawLang =
    t?.lang ||
    t?.language ||
    t?.currentLang ||
    (typeof window !== "undefined"
      ? window.localStorage.getItem("langue") ||
        window.localStorage.getItem("language") ||
        window.localStorage.getItem("lang")
      : null);

  const normalizedLang = String(rawLang || "fr").slice(0, 2).toLowerCase();

  return SUPPORTED_HERO_LANGS.includes(normalizedLang) ? normalizedLang : "fr";
}

/**
 * ============================================================
 * ICÔNE — FONDUE
 * ============================================================
 */
function FondueIcon() {
  return (
    <svg viewBox="0 0 64 64" className="hero-action-svg" aria-hidden="true">
      <path d="M18 29h28v8c0 8-6 14-14 14s-14-6-14-14v-8Z" />
      <path d="M14 29h36" />
      <path d="M22 51h20" />
      <path d="M25 18c-3-4 3-6 0-10" />
      <path d="M33 18c-3-4 3-6 0-10" />
      <path d="M41 18c-3-4 3-6 0-10" />
    </svg>
  );
}

/**
 * ============================================================
 * ICÔNE — PLATEAU
 * ============================================================
 */
function PlateauIcon() {
  return (
    <svg viewBox="0 0 64 64" className="hero-action-svg" aria-hidden="true">
      <path d="M12 40h40" />
      <path d="M16 40c0-10 8-18 16-18s16 8 16 18" />
      <circle cx="32" cy="20" r="2.5" />
      <path d="M10 44h44" />
    </svg>
  );
}

/**
 * ============================================================
 * ICÔNES — PROMESSE POST-SCAN
 * ============================================================
 */
function CheeseIcon() {
  return (
    <svg viewBox="0 0 64 64" className="hero-trust-svg" aria-hidden="true">
      <path d="M12 40h40v10H12z" />
      <path d="M12 40 28 22h24v18" />
      <circle cx="25" cy="39" r="2" />
      <circle cx="34" cy="34" r="2" />
      <circle cx="44" cy="39" r="2" />
    </svg>
  );
}

function BottlesIcon() {
  return (
    <svg viewBox="0 0 64 64" className="hero-trust-svg" aria-hidden="true">
      <path d="M18 24h10v28H18z" />
      <path d="M20 14h6v10h-6z" />
      <path d="M36 20h10v32H36z" />
      <path d="M38 10h6v10h-6z" />
      <circle cx="23" cy="38" r="2" />
      <circle cx="41" cy="38" r="2" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg viewBox="0 0 64 64" className="hero-trust-svg" aria-hidden="true">
      <path d="M14 18h24l12 12-24 24L14 42z" />
      <circle cx="32" cy="26" r="3" />
    </svg>
  );
}

/**
 * ============================================================
 * BOUTON — ACTION HERO
 * ============================================================
 */
function HeroActionButton({ icon, label, onClick }) {
  return (
    <button type="button" onClick={onClick} className="hero-action-card">
      <span className="hero-action-icon">{icon}</span>
      <span className="hero-action-label">{label}</span>
    </button>
  );
}

/**
 * ============================================================
 * COMPOSANT — HERO
 * ============================================================
 */
export default function HeroSection({
  t,
  produitsAffiches,
  setVueActuelle,
  handleActionNavigation,
}) {
  const [heroLang, setHeroLang] = useState(() => getHeroLang(t));
  const [heroScrollProgress, setHeroScrollProgress] = useState(0);

  useEffect(() => {
    let rafId = null;

    const updateScroll = () => {
      const maxScroll = 420;
      const progress = Math.min(window.scrollY / maxScroll, 1);

      setHeroScrollProgress((current) => {
        if (Math.abs(current - progress) < 0.002) return current;
        return progress;
      });

      rafId = null;
    };

    const handleScroll = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(updateScroll);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, []);

  useEffect(() => {
    setHeroLang(getHeroLang(t));
  }, [t]);

  useEffect(() => {
    const refreshHeroLang = () => {
      setHeroLang(getHeroLang(t));
    };

    window.addEventListener("focus", refreshHeroLang);
    window.addEventListener("click", refreshHeroLang);

    return () => {
      window.removeEventListener("focus", refreshHeroLang);
      window.removeEventListener("click", refreshHeroLang);
    };
  }, [t]);

  const selectionMomentImage = useMemo(
    () => `${HERO_IMAGE_BASE_URL}/hero-selection-moment-${heroLang}.png`,
    [heroLang]
  );

  const universCaveImage = useMemo(
    () => `${HERO_IMAGE_BASE_URL}/hero-univers-cave-${heroLang}.png`,
    [heroLang]
  );

  return (
    <div
      className="hero-section-luxury page-transition hero-section-editorial"
      style={{
        "--hero-scroll-progress": heroScrollProgress,
        "--hero-badge-scale": 1 - heroScrollProgress * 0.68,
        "--hero-badge-opacity": 1 - heroScrollProgress * 0.5,
        "--hero-badge-space": `${450 - heroScrollProgress * 360}px`,
        "--hero-badge-space-mobile": `${250 - heroScrollProgress * 190}px`,
        "--hero-image-width": `${74 + heroScrollProgress * 26}%`,
        "--hero-image-scale": 0.9 + heroScrollProgress * 0.1,
      }}
    >
      <div className="hero-bg" aria-hidden="true" />

      <div className="hero-content">
        {/* BADGE STERCHI */}
        <div className="hero-badge-row">
          <div className="hero-badge-shell">
            <img
              src="/images/badge-sterchi.png"
              alt="Maison du Fromage Sterchi"
              className="hero-sterchi-badge"
            />
          </div>
        </div>

        {/* MESSAGE POST-SCAN */}
        <div className="hero-scan-message">
          <h1 className="hero-scan-title">
            <span>{t.title}</span>
            <strong>{t.subtitle}</strong>
          </h1>
        </div>

        <div className="hero-trust-line" aria-hidden="true">
          <span />
        </div>

        <div className="hero-trust-subtitle">
          {t.heroTrustSubtitle ||
            "Des sélections choisies avec passion par Cédric Naudin."}
        </div>

        <div className="hero-trust-grid">
          <div className="hero-trust-item">
            <CheeseIcon />
            <span>{t.heroTrustCheese || "FROMAGES D’EXCEPTION"}</span>
          </div>

          <div className="hero-trust-item">
            <BottlesIcon />
            <span>{t.heroTrustCave || "SÉLECTIONS CAVE"}</span>
          </div>

          <div className="hero-trust-item">
            <CheeseIcon />
            <span>{t.heroTrustPlateaux || "PLATEAUX PERSONNALISÉS"}</span>
          </div>

          <div className="hero-trust-item">
            <TagIcon />
            <span>{t.heroTrustOffers || "OFFRES PRIVÉES"}</span>
          </div>
        </div>

        {/* SÉLECTION DU MOMENT */}
        <button
          type="button"
          className="hero-image-card hero-selection-card"
          onClick={() => setVueActuelle("magasin")}
          aria-label={t?.discover || "Découvrir la sélection"}
        >
          <img
            key={selectionMomentImage}
            src={selectionMomentImage}
            alt=""
            aria-hidden="true"
            className="hero-image-card-img"
          />
        </button>

        {/* UNIVERS CAVE */}
        <button
          type="button"
          className="hero-image-card hero-cave-card"
          onClick={() => setVueActuelle("alcool")}
          aria-label={t?.wineUniverse || "Découvrir notre univers cave"}
        >
          <img
            key={universCaveImage}
            src={universCaveImage}
            alt=""
            aria-hidden="true"
            className="hero-image-card-img"
          />
        </button>

        {/* PLATEAU */}
        <div className="hero-main-actions">
          <HeroActionButton
            icon={<PlateauIcon />}
            label={t?.eventPlattersShort || "CONFIGUREZ VOTRE PLATEAU ÉVÉNEMENT"}
            onClick={() => setVueActuelle("plateaux-evenements")}
          />
        </div>

        {/* BANNIÈRE ACTION */}
        <div className="hero-action-banner-wrap">
          <HomeActionBanner
            t={t}
            produits={produitsAffiches}
            onActionClick={handleActionNavigation}
            setVueActuelle={setVueActuelle}
          />
        </div>

        {SHOW_PLATTER_VIDEO_HOME ? (
          <div className="hero-platter-wrap">
            <PlatterVideoOverlay />
          </div>
        ) : null}
      </div>

      <style jsx="true">{`
        /* ============================================================
           STRUCTURE GLOBALE HERO
           ============================================================ */
        .hero-section-editorial {
          position: relative;
          z-index: 1;
          overflow-x: hidden;
          overflow-y: visible;
          background: transparent;
          isolation: isolate;
        }

        .hero-bg {
          position: fixed;
          inset: 0;
          z-index: 0;
          pointer-events: none;
          background-image: linear-gradient(
              180deg,
              rgba(251, 218, 211, 0.25) 0%,
              rgba(251, 248, 241, 0.75) 15%,
              rgba(251, 248, 241, 0.45) 45%,
              rgba(251, 248, 241, 0.25) 75%,
              rgba(251, 248, 241, 0.15) 100%
            ),
            url("https://app.marchedeluxe.ch/images/ArrierePlanPageHome.png");
          background-size: cover;
          background-position: center top;
          background-repeat: no-repeat;
        }

        .hero-section-editorial .hero-content {
          width: min(100%, 920px);
          margin: 0 auto;
          position: relative;
          z-index: 2;
          padding-top: 0;
        }

        /* ============================================================
           BADGE STERCHI
           ============================================================ */
        .hero-section-editorial .hero-badge-row {
          width: 100%;
          height: var(--hero-badge-space);
          display: flex;
          justify-content: center;
          align-items: center;
          box-sizing: border-box;
          margin-bottom: 2px;
          transition: none;
        }

        .hero-section-editorial .hero-badge-shell {
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .hero-section-editorial .hero-sterchi-badge {
          width: clamp(330px, 52vw, 300px);
          opacity: var(--hero-badge-opacity);
          transform: scale(var(--hero-badge-scale));
          transform-origin: center center;
          filter: drop-shadow(0 8px 20px rgba(0, 0, 0, 0.18));
          pointer-events: none;
          display: block;
          transition: none;
        }

        /* ============================================================
           MESSAGE POST-SCAN — TITRE PRINCIPAL
           ============================================================ */
        .hero-section-editorial .hero-scan-message {
          width: calc(100% - 34px);
          max-width: 760px;
          margin: -6px auto 12px auto;
          text-align: center;
        }

        .hero-section-editorial .hero-scan-title {
          margin: 0;
          font-family: "Playfair Display", serif !important;
          font-weight: 700 !important;
          line-height: 1.06 !important;
          text-align: center !important;
          color: #111 !important;
          text-shadow: 0 1px 0 rgba(255, 255, 255, 0.72),
            0 2px 4px rgba(70, 50, 20, 0.12);
        }

        .hero-section-editorial .hero-scan-title span,
        .hero-section-editorial .hero-scan-title strong {
          display: block;
        }

        .hero-section-editorial .hero-scan-title span {
          font-size: clamp(2rem, 5vw, 3.15rem);
          letter-spacing: -0.02em;
        }

        .hero-section-editorial .hero-scan-title strong {
          margin-top: 2px;
          font-size: clamp(1.45rem, 3.9vw, 2.35rem);
          font-weight: 700;
          color: #a8843f;
          letter-spacing: -0.015em;
        }

        /* ============================================================
           LIGNE + ICÔNES PROMESSE
           ============================================================ */
        .hero-section-editorial .hero-trust-line {
          display: flex;
          justify-content: center;
          align-items: center;
          margin: 12px auto 8px;
        }

        .hero-section-editorial .hero-trust-line::before,
        .hero-section-editorial .hero-trust-line::after {
          content: "";
          width: 120px;
          height: 1px;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(168, 132, 63, 0.65),
            transparent
          );
        }

        .hero-section-editorial .hero-trust-line span::before {
          content: "✹";
          margin: 0 10px;
          color: #a8843f;
          font-size: 0.95rem;
        }

        .hero-section-editorial .hero-trust-subtitle {
          margin: 0 auto 18px;
          text-align: center;
          font-family: "Playfair Display", serif;
          font-size: clamp(0.95rem, 1.8vw, 1.15rem);
          font-weight: 700;
          color: #2a241d;
          letter-spacing: 0.02em;
        }

        .hero-section-editorial .hero-trust-grid {
          width: calc(100% - 34px);
          max-width: 760px;
          margin: 0 auto 24px;
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 18px;
        }

        .hero-section-editorial .hero-trust-item {
          display: grid;
          justify-items: center;
          gap: 8px;
          text-align: center;
          color: #a8843f;
        }

        .hero-section-editorial .hero-trust-svg {
          width: 42px;
          height: 42px;
          fill: none;
          stroke: #a8843f;
          stroke-width: 3;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .hero-section-editorial .hero-trust-item span {
          font-size: 0.74rem;
          font-weight: 800;
          letter-spacing: 0.055em;
          line-height: 1.25;
          color: #2a241d;
          text-transform: uppercase;
        }

        /* ============================================================
           CARTES IMAGE
           ============================================================ */
        .hero-section-editorial .hero-image-card {
          width: min(var(--hero-image-width), 860px);
          transform: scale(var(--hero-image-scale));
          transform-origin: center top;
          aspect-ratio: 1672 / 941;
          height: auto;
          margin: 0 auto;
          padding: 0;
          border: 1px solid rgba(168, 138, 76, 0.2);
          border-radius: 18px;
          overflow: hidden;
          display: block;
          cursor: pointer;
          background: rgba(247, 241, 231, 0.72);
          box-shadow: 0 12px 26px rgba(50, 38, 24, 0.12),
            0 2px 6px rgba(50, 38, 24, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.55);
          transition: transform 0.22s ease, box-shadow 0.22s ease,
            border-color 0.22s ease;
        }

        .hero-section-editorial .hero-image-card:hover {
          transform: scale(var(--hero-image-scale)) translateY(-2px);
          border-color: rgba(168, 138, 76, 0.34);
          box-shadow: 0 16px 34px rgba(50, 38, 24, 0.15),
            0 4px 10px rgba(50, 38, 24, 0.08),
            inset 0 1px 0 rgba(255, 255, 255, 0.65);
        }

        .hero-section-editorial .hero-image-card-img {
          width: 100%;
          height: 100%;
          display: block;
          object-fit: cover;
          object-position: center;
          animation: heroImageSoftChange 0.38s ease both;
        }

        @keyframes heroImageSoftChange {
          0% {
            opacity: 0;
            transform: scale(1.015);
            filter: blur(4px);
          }

          100% {
            opacity: 1;
            transform: scale(1);
            filter: blur(0);
          }
        }

        .hero-section-editorial .hero-cave-card {
          margin-top: 18px;
        }

        /* ============================================================
           BOUTON PLATEAU
           ============================================================ */
        .hero-section-editorial .hero-main-actions {
          display: grid;
          grid-template-columns: 1fr;
          gap: 18px 22px;
          width: calc(100% - 28px);
          max-width: 760px;
          margin: 24px auto 0 auto;
        }

        .hero-section-editorial .hero-action-card {
          min-height: 94px;
          border-radius: 24px;
          border: 1px solid rgba(168, 138, 76, 0.18);
          background: rgba(251, 248, 241, 0.098);
          color: rgba(18, 18, 18, 0.94);
          display: grid;
          grid-template-columns: 74px minmax(0, 1fr);
          align-items: center;
          text-align: left;
          padding: 18px 24px;
          cursor: pointer;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-shadow: 0 1px 0 rgba(255, 255, 255, 0.7) inset,
            0 10px 22px rgba(84, 66, 38, 0.06);
          transition: transform 0.22s ease, box-shadow 0.22s ease,
            border-color 0.22s ease;
        }

        .hero-section-editorial .hero-action-card:hover {
          transform: translateY(-2px);
          border-color: rgba(168, 138, 76, 0.4);
          box-shadow: 0 1px 0 rgba(255, 255, 255, 0.75) inset,
            0 16px 30px rgba(84, 66, 38, 0.08);
        }

        .hero-section-editorial .hero-action-svg {
          width: 38px;
          height: 38px;
          fill: none;
          stroke: #a8843f;
          stroke-width: 3.4;
          stroke-linecap: round;
          stroke-linejoin: round;
        }

        .hero-section-editorial .hero-action-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .hero-section-editorial .hero-action-label {
          min-width: 0;
          font-family: inherit;
          font-size: clamp(0.76rem, 1.4vw, 0.98rem);
          font-weight: 500;
          letter-spacing: clamp(0.08em, 0.7vw, 0.18em);
          line-height: 1.35;
          text-transform: uppercase;
          overflow-wrap: anywhere;
        }

        .hero-section-editorial .hero-action-banner-wrap {
          margin-top: 24px;
        }

        .hero-section-editorial .hero-platter-wrap {
          margin-top: 22px;
          display: flex;
          justify-content: center;
        }

        /* ============================================================
           TABLETTE
           ============================================================ */
        @media (max-width: 768px) {
          .hero-bg {
            background-size: cover;
            background-position: center top;
          }

          .hero-section-editorial .hero-sterchi-badge {
            width: clamp(320px, 88vw, 350px);
          }

          .hero-section-editorial .hero-image-card {
            width: calc(100% - 24px);
            height: 250px;
            aspect-ratio: auto;
          }

          .hero-section-editorial .hero-main-actions {
            width: calc(100% - 24px);
            gap: 14px;
          }

          .hero-section-editorial .hero-action-card {
            min-height: 84px;
            grid-template-columns: 58px minmax(0, 1fr);
            padding: 16px 18px;
            border-radius: 21px;
          }

          .hero-section-editorial .hero-action-svg {
            width: 34px;
            height: 34px;
          }
        }

        /* ============================================================
           MOBILE
           ============================================================ */
        @media (max-width: 520px) {
          .hero-bg {
            background-size: auto 100%;
            background-position: center top;
          }

          .hero-section-editorial .hero-badge-row {
            height: var(--hero-badge-space-mobile);
            align-items: center;
          }

          .hero-section-editorial .hero-sterchi-badge {
            width: clamp(220px, 80vw, 210px);
          }

          .hero-section-editorial .hero-scan-message {
            width: calc(100% - 30px);
            margin: -4px auto 12px auto;
          }

          .hero-section-editorial .hero-scan-title span {
            font-size: 2rem;
          }

          .hero-section-editorial .hero-scan-title strong {
            font-size: 1.35rem;
            line-height: 1.12;
          }

          .hero-section-editorial .hero-trust-line {
            margin: 10px auto 6px;
          }

          .hero-section-editorial .hero-trust-line::before,
          .hero-section-editorial .hero-trust-line::after {
            width: 76px;
          }

          .hero-section-editorial .hero-trust-subtitle {
            font-size: 0.92rem;
            margin-bottom: 14px;
          }

          .hero-section-editorial .hero-trust-grid {
            width: calc(100% - 30px);
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 8px;
            margin-bottom: 20px;
          }

          .hero-section-editorial .hero-trust-svg {
            width: 30px;
            height: 30px;
            stroke-width: 3.1;
          }

          .hero-section-editorial .hero-trust-item span {
            font-size: 0.54rem;
            letter-spacing: 0.04em;
            line-height: 1.2;
          }

          .hero-section-editorial .hero-image-card {
            width: calc(100% - 28px);
            height: 180px;
            aspect-ratio: auto;
            border-radius: 16px;
          }

          .hero-section-editorial .hero-selection-card .hero-image-card-img {
            object-position: center center;
          }

          .hero-section-editorial .hero-cave-card .hero-image-card-img {
            object-position: center center;
          }

          .hero-section-editorial .hero-main-actions {
            gap: 10px;
            width: calc(100% - 28px);
            margin-top: 18px;
          }

          .hero-section-editorial .hero-action-card {
            min-height: 96px;
            grid-template-columns: 1fr;
            grid-template-rows: auto auto;
            justify-items: center;
            text-align: center;
            padding: 14px 9px;
            border-radius: 18px;
          }

          .hero-section-editorial .hero-action-svg {
            width: 32px;
            height: 32px;
          }

          .hero-section-editorial .hero-action-label {
            font-size: clamp(0.58rem, 2.6vw, 0.7rem);
            letter-spacing: 0.08em;
            line-height: 1.28;
          }

          .hero-section-editorial .hero-action-banner-wrap {
            margin-top: 20px;
          }

          .hero-section-editorial .hero-platter-wrap {
            margin-top: 18px;
          }
        }
      `}</style>
    </div>
  );
}